import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X } from 'lucide-react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { supabase } from '../supabase';
import {
  calculatePropertyScore,
  type PropertyForScoring,
  type NeighborhoodScoreRow,
  type NeighborhoodStatsRow,
  type PropertyScoreResult,
  type QualityBreakdownItem,
} from '../utils/propertyScoreEngine';

// ── Types ────────────────────────────────────────────────────────────────────

interface ComparableTransaction {
  full_address: string | null;
  size_sqm: number | null;
  rooms: number | null;
  floor: string | null;
  price: number | null;
  price_per_sqm: number | null;
  transaction_date: string | null;
}

interface PricingAnalysis {
  askingPrice: number;
  size: number;
  pricePerSqm: number;
  benchmarkPerSqm: number;
  benchmarkLabel: string;
  gapPercent: number;
  status: string;
}

interface RvRecommendedPrice {
  baseMarketPrice: number;
  qualityScore: number;
  qualityAdjustmentPercent: number;
  recommendedPrice: number;
  recommendedGapPercent: number;
  status: string;
}

interface OfficeProfile {
  name: string;
  logo_url?: string | null;
  address?: string | null;
  phone?: string | null;
  secondary_phone?: string | null;
  email?: string | null;
  website?: string | null;
}

interface AdvisorResult {
  executive_summary: string;
  recommended_marketing_strategy: string;
  key_selling_points: string[];
  risk_factors: string[];
  marketability_assessment: {
    level: string;
    explanation: string;
  };
  pricing_recommendation?: string;
  action_items?: string[];
}

interface ReportData {
  result: PropertyScoreResult;
  nsRow: NeighborhoodScoreRow | null;
  statsRow: NeighborhoodStatsRow | null;
  comparables: ComparableTransaction[];
  pricing: PricingAnalysis | null;
  rvRecommended: RvRecommendedPrice | null;
  propertyAddress: string | null;
  propertyType: string | null;
  reportDate: string;
}

export interface PropertyRatingPrintReportProps {
  formId: string;
  city: string;
  neighborhood: string;
  onClose: () => void;
  advisor?: AdvisorResult | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function formatPrice(n: number): string {
  return `₪${Math.round(n).toLocaleString('he-IL')}`;
}

function cleanNeighborhoodText(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/^"+|"+$/g, '').replace(/\\"/g, '"').trim();
}

function parseNeighborhoodItems(raw: string | null | undefined): string[] {
  const cleaned = cleanNeighborhoodText(raw);
  if (!cleaned) return [];
  return cleaned.split(',').map(s => s.trim()).filter(Boolean);
}

function buildPricingAnalysis(
  askingPrice: number,
  size: number,
  stats: NeighborhoodStatsRow | null,
): PricingAnalysis | null {
  if (!askingPrice || !size) return null;
  const pricePerSqm = askingPrice / size;
  const benchmarkPerSqm = stats?.median_price_per_sqm_transactions ?? stats?.avg_price_per_sqm ?? null;
  if (!benchmarkPerSqm) return null;
  const benchmarkLabel = stats?.median_price_per_sqm_transactions != null
    ? 'חציון עסקאות בפועל'
    : 'ממוצע לפי מקורות נדל"ן';
  const gapPercent = Math.round(((pricePerSqm - benchmarkPerSqm) / benchmarkPerSqm) * 1000) / 10;
  let status: string;
  if (gapPercent <= -10) status = 'הזדמנות – מתחת למחיר השוק';
  else if (gapPercent <= -3) status = 'מתומחר אטרקטיבית';
  else if (gapPercent < 3) status = 'מתומחר נכון';
  else if (gapPercent < 10) status = 'מעט מעל מחיר השוק';
  else if (gapPercent < 20) status = 'יקר ביחס לשוק';
  else status = 'יקר משמעותית ביחס לשוק';
  return { askingPrice, size, pricePerSqm, benchmarkPerSqm, benchmarkLabel, gapPercent, status };
}

function buildRvRecommendedPrice(
  askingPrice: number,
  size: number,
  stats: NeighborhoodStatsRow | null,
  qualityScore: number,
): RvRecommendedPrice | null {
  if (!size) return null;
  const benchmarkPerSqm = stats?.median_price_per_sqm_transactions ?? stats?.avg_price_per_sqm ?? null;
  if (!benchmarkPerSqm) return null;
  const baseMarketPrice = benchmarkPerSqm * size;
  let qualityAdjustmentPercent: number;
  if (qualityScore >= 90) qualityAdjustmentPercent = 8;
  else if (qualityScore >= 80) qualityAdjustmentPercent = 5;
  else if (qualityScore >= 70) qualityAdjustmentPercent = 2;
  else if (qualityScore >= 60) qualityAdjustmentPercent = 0;
  else if (qualityScore >= 50) qualityAdjustmentPercent = -3;
  else qualityAdjustmentPercent = -6;
  const rawRecommended = baseMarketPrice * (1 + qualityAdjustmentPercent / 100);
  const recommendedPrice = Math.round(rawRecommended / 10000) * 10000;
  const recommendedGapPercent = askingPrice && recommendedPrice
    ? Math.round(((askingPrice - recommendedPrice) / recommendedPrice) * 1000) / 10
    : 0;
  let status: string;
  if (recommendedGapPercent <= -5) status = 'המחיר המבוקש נמוך מהמחיר המומלץ';
  else if (recommendedGapPercent < 5) status = 'המחיר המבוקש תואם למחיר המומלץ';
  else if (recommendedGapPercent < 10) status = 'המחיר המבוקש מעט מעל המחיר המומלץ';
  else if (recommendedGapPercent < 20) status = 'המחיר המבוקש גבוה מהמחיר המומלץ';
  else status = 'המחיר המבוקש גבוה משמעותית מהמחיר המומלץ';
  return { baseMarketPrice, qualityScore, qualityAdjustmentPercent, recommendedPrice, recommendedGapPercent, status };
}

// ── Logo Image with Load Tracking ────────────────────────────────────────────

interface LogoImgProps {
  src: string;
  alt: string;
  onLoaded: () => void;
  onError: () => void;
}

function LogoImg({ src, alt, onLoaded, onError }: LogoImgProps) {
  return (
    <img
      src={src}
      alt={alt}
      className="rv-office-logo"
      onLoad={onLoaded}
      onError={onError}
    />
  );
}

// ── Component ────────────────────────────────────────────────────────────────

function PropertyRatingPrintReport({
  formId,
  city,
  neighborhood,
  onClose,
  advisor,
}: PropertyRatingPrintReportProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReportData | null>(null);
  const [office, setOffice] = useState<OfficeProfile | null>(null);
  const [agentName, setAgentName] = useState<string | null>(null);
  const [userLogoUrl, setUserLogoUrl] = useState<string | null>(null);
  const [userBranchName, setUserBranchName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState<string | null>(null);

  // Logo load tracking
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Print timing
  const [printPending, setPrintPending] = useState(false);
  const printPendingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const trimCity = city.trim();
        const trimNeigh = (neighborhood || '').trim();

        const [firestoreSnap, scoresRes, statsRes, comparablesRes] = await Promise.all([
          getDoc(doc(db, 'forms', formId)),
          supabase
            .from('market_neighborhood_scores')
            .select('location_score, confidence, location_level, reason, strengths, weaknesses')
            .eq('city', trimCity)
            .eq('neighborhood_name', trimNeigh)
            .maybeSingle(),
          supabase
            .from('market_neighborhood_stats')
            .select('avg_price_per_sqm, transactions_count, data_period, avg_price_per_sqm_transactions, median_price_per_sqm_transactions, transactions_count_12m, transactions_stats_period, active_listings_count, active_listings_updated_at')
            .eq('city', trimCity)
            .eq('neighborhood_name', trimNeigh)
            .maybeSingle(),
          supabase
            .from('market_comparable_transactions')
            .select('full_address, size_sqm, rooms, floor, price, price_per_sqm, transaction_date')
            .eq('city', trimCity)
            .eq('neighborhood_name', trimNeigh)
            .order('transaction_date', { ascending: false })
            .limit(5),
        ]);

        if (cancelled) return;

        if (!firestoreSnap.exists()) {
          setError('לא נמצאו נתוני נכס');
          return;
        }

        const formData = firestoreSnap.data();
        const p = formData.property || {};

        const addrStreet = [formData.address?.street, formData.address?.houseNumber].filter(Boolean).join(' ');
        const propertyType = p.propertyType || formData.propertyType || null;

        const property: PropertyForScoring = {
          propertyType: p.propertyType || formData.propertyType,
          propertyAge: p.propertyAge || formData.propertyAge,
          propertyCondition: p.propertyCondition || formData.propertyCondition,
          size: p.size || formData.size,
          rooms: p.rooms || formData.rooms,
          bathrooms: p.bathrooms || formData.bathrooms,
          floor: p.floor ?? formData.floor,
          parkingSpots: p.parkingSpots ?? formData.parkingSpots,
          hasElevator: p.hasElevator ?? formData.hasElevator,
          hasSaferoom: p.hasSaferoom ?? formData.hasSaferoom,
          hasBalcony: p.hasBalcony ?? formData.hasBalcony,
          balconySize: p.balconySize ?? formData.balconySize,
          view: p.view || formData.view,
          airDirection: p.airDirection || formData.airDirection,
          airDirectionLabel: p.airDirectionLabel || formData.airDirectionLabel,
          nearbyPlaces: formData.nearbyPlaces,
        };

        const ns: NeighborhoodScoreRow | null = scoresRes.data ?? null;
        const st: NeighborhoodStatsRow | null = statsRes.data ?? null;
        const scoreResult = calculatePropertyScore(property, ns, st);

        const askingPrice = Number(p.price || formData.price || 0);
        const size = Number(p.size || formData.size || 0);
        const pricing = size ? buildPricingAnalysis(askingPrice, size, st) : null;
        const rvRecommended = size ? buildRvRecommendedPrice(askingPrice, size, st, scoreResult.qualityResult.qualityScore) : null;

        setData({
          result: scoreResult,
          nsRow: ns,
          statsRow: st,
          comparables: comparablesRes.data ?? [],
          pricing,
          rvRecommended,
          propertyAddress: addrStreet || null,
          propertyType,
          reportDate: new Date().toLocaleDateString('he-IL'),
        });

        // Fetch office profile + agent name in parallel
        try {
          const user = auth.currentUser;
          if (user) {
            const idToken = await user.getIdToken();
            const [officeRes, profileSnap] = await Promise.all([
              fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-office`, {
                headers: { Authorization: `Bearer ${idToken}` },
              }),
              getDoc(doc(db, 'users', user.uid)),
            ]);

            if (!cancelled && officeRes.ok) {
              const officeData = await officeRes.json();
              if (officeData.success && officeData.office) {
                setOffice({
                  name: officeData.office.name,
                  logo_url: officeData.office.logo_url,
                  address: officeData.office.address,
                  phone: officeData.office.phone,
                  secondary_phone: officeData.office.secondary_phone,
                  email: officeData.office.email,
                  website: officeData.office.website,
                });
              }
            }

            if (!cancelled && profileSnap.exists()) {
              const profileData = profileSnap.data();
              setAgentName(profileData.fullName || profileData.displayName || null);
              setUserEmail(profileData.email || null);
              setUserBranchName(profileData.branchName || null);
              setUserLogoUrl(profileData.logoUrl || null);
              setUserPhone(profileData.mobile || profileData.phone || profileData.phoneNumber || null);
            }
          }
        } catch {
          // Office/agent data is optional
        }
      } catch (e) {
        if (!cancelled) setError('שגיאה בטעינת נתוני הדו"ח');
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [formId, city, neighborhood]);

  // ── Print: wait for DOM + logo before calling window.print() ──────────────

  const handlePrintClick = useCallback(() => {
    setPrintPending(true);
    printPendingRef.current = true;
  }, []);

  useEffect(() => {
    if (!printPending) return;
    if (loading || error || !data) return;

    // If no logo URL, logo is "ready" (nothing to wait for)
    const hasLogo = !!(userLogoUrl || office?.logo_url);
    if (hasLogo && !logoLoaded && !logoError) return;

    // Report DOM + logo are ready — double rAF ensures paint before print
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const roots = document.querySelectorAll('#property-rating-print-report');
        console.log('print roots', roots.length);
        if (roots.length === 1 && roots[0]) {
          window.print();
        }
        setPrintPending(false);
        printPendingRef.current = false;
      });
    });

    return () => cancelAnimationFrame(raf1);
  }, [printPending, loading, error, data, logoLoaded, logoError, office, userLogoUrl]);

  // ── Render helpers ──────────────────────────────────────────────────────────

  const scoreColor = (label: string): string => {
    if (label === 'מצוין') return '#16a34a';
    if (label === 'טוב') return '#2563eb';
    if (label === 'בינוני') return '#ca8a04';
    return '#dc2626';
  };

  const barColor = (pct: number): string => {
    if (pct >= 75) return '#16a34a';
    if (pct >= 50) return '#2563eb';
    if (pct >= 35) return '#ca8a04';
    return '#dc2626';
  };

  const strengths = data ? data.result.strengths : [];
  const weaknesses = data ? data.result.weaknesses : [];
  const nsStrengths = data ? parseNeighborhoodItems(data.nsRow?.strengths as string | null | undefined) : [];
  const nsWeaknesses = data ? parseNeighborhoodItems(data.nsRow?.weaknesses as string | null | undefined) : [];

  const handleLogoLoaded = useCallback(() => setLogoLoaded(true), []);
  const handleLogoError = useCallback(() => setLogoError(true), []);

  const resolvedLogoUrl = userLogoUrl || office?.logo_url || null;
  const resolvedOfficeName = userBranchName || office?.name || null;
  const resolvedAgentName = agentName;
  const resolvedEmail = userEmail || office?.email || null;
  const resolvedAddress = office?.address || null;
  const resolvedPhone = office?.phone || null;
  const resolvedSecondaryPhone = office?.secondary_phone || null;
  const resolvedWebsite = office?.website || null;
  const resolvedUserPhone = userPhone || null;

  const reportProps = {
    data: data!,
    resolvedLogoUrl,
    resolvedOfficeName,
    resolvedAgentName,
    resolvedEmail,
    resolvedAddress,
    resolvedPhone,
    resolvedSecondaryPhone,
    resolvedWebsite,
    resolvedUserPhone,
    advisor,
    city,
    neighborhood,
    strengths,
    weaknesses,
    nsStrengths,
    nsWeaknesses,
    scoreColor,
    barColor,
    logoLoaded,
    logoError,
    onLogoLoaded: handleLogoLoaded,
    onLogoError: handleLogoError,
  };

  return createPortal(
    <>
      {/* Screen-only overlay — hidden in print */}
      <div className="rv-report-screen-preview fixed inset-0 bg-black/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4" dir="rtl">
        <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col border border-gray-700">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-700 shrink-0">
            <div className="flex items-center gap-2">
              <Printer size={18} className="text-blue-400" />
              <span className="text-white font-bold text-sm">תצוגת הדפסה — דו"ח דירוג נכס</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrintClick}
                disabled={loading || !!error || printPending}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors text-xs font-medium"
              >
                <Printer size={14} />
                {printPending ? 'מכין דו"ח...' : 'הדפס / שמור כ-PDF'}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Browser header/footer note */}
          <div className="px-5 py-1.5 bg-yellow-900/20 border-b border-yellow-700/30 text-xs text-yellow-400 text-center">
            כדי להסיר את כתובת האתר והתאריך מה-PDF, בטל את האפשרות 'כותרות עליונות ותחתונות' בחלון ההדפסה.
          </div>

          {/* Scrollable report preview */}
          <div className="overflow-y-auto flex-1 bg-gray-200 p-6">
            {loading && (
              <div className="flex items-center justify-center py-20 text-gray-500">
                <span className="text-sm">טוען נתוני דו"ח...</span>
              </div>
            )}
            {error && (
              <div className="flex items-center justify-center py-20 text-red-500">
                <span className="text-sm">{error}</span>
              </div>
            )}
            {data && !loading && (
              <div className="mx-auto bg-white shadow-lg" style={{ maxWidth: '210mm', minHeight: '297mm' }}>
                <PrintableReport {...reportProps} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Print-only report — hidden on screen, visible only in print */}
      {data && !loading && !error && (
        <div id="property-rating-print-report" className="rv-report-print-root">
          <PrintableReport {...reportProps} />
        </div>
      )}
    </>,
    document.body,
  );
}

// ── Printable Report ──────────────────────────────────────────────────────────

interface PrintableReportProps {
  data: ReportData;
  resolvedLogoUrl: string | null;
  resolvedOfficeName: string | null;
  resolvedAgentName: string | null;
  resolvedEmail: string | null;
  resolvedAddress: string | null;
  resolvedPhone: string | null;
  resolvedSecondaryPhone: string | null;
  resolvedWebsite: string | null;
  resolvedUserPhone: string | null;
  advisor: AdvisorResult | null;
  city: string;
  neighborhood: string;
  strengths: string[];
  weaknesses: string[];
  nsStrengths: string[];
  nsWeaknesses: string[];
  scoreColor: (label: string) => string;
  barColor: (pct: number) => string;
  logoLoaded: boolean;
  logoError: boolean;
  onLogoLoaded: () => void;
  onLogoError: () => void;
}

function PrintableReport({
  data,
  resolvedLogoUrl,
  resolvedOfficeName,
  resolvedAgentName,
  resolvedEmail,
  resolvedAddress,
  resolvedPhone,
  resolvedSecondaryPhone,
  resolvedWebsite,
  resolvedUserPhone,
  advisor,
  city,
  neighborhood,
  strengths,
  weaknesses,
  nsStrengths,
  nsWeaknesses,
  scoreColor,
  barColor,
  logoLoaded,
  logoError,
  onLogoLoaded,
  onLogoError,
}: PrintableReportProps) {
  const { result, nsRow, statsRow, comparables, pricing, rvRecommended, propertyAddress, propertyType, reportDate } = data;

  const showLogo = resolvedLogoUrl && !logoError;
  const showFallback = !showLogo && resolvedOfficeName;

  // ── Display label maps (internal values stay unchanged) ──
  const propertyTypeLabel = (t: string | null) => {
    if (!t) return null;
    const map: Record<string, string> = { 'apartment': 'דירה', 'house': 'בית', 'cottage': 'קוטג' };
    return map[t.toLowerCase()] ?? t;
  };
  const confidenceLabel = (c: string | null) => {
    if (!c) return null;
    const map: Record<string, string> = { 'strong': 'חזק', 'medium': 'בינונית', 'weak': 'חלשה' };
    return map[c.toLowerCase()] ?? c;
  };
  const periodLabel = (p: string | null) => {
    if (!p) return null;
    return p.replace(/rolling\s*12\s*m/i, '12 חודשים אחרונים');
  };

  return (
    <div className="rv-print-report rv-report-document" dir="rtl">
      {/* ── Section 1: Office Header ── */}
      <div className="rv-section rv-office-header">
        <div className="rv-office-logo-row">
          {showLogo && (
            <LogoImg
              src={resolvedLogoUrl!}
              alt={resolvedOfficeName || 'לוגו משרד'}
              onLoaded={onLogoLoaded}
              onError={onLogoError}
            />
          )}
          {showFallback && (
            <div className="rv-office-logo-fallback">
              {resolvedOfficeName}
            </div>
          )}
          {!showLogo && !showFallback && (
            <div className="rv-office-logo-placeholder" />
          )}
          <div className="rv-office-info">
            {resolvedOfficeName && <div className="rv-office-name">{resolvedOfficeName}</div>}
            {resolvedAgentName && <div className="rv-office-agent">{resolvedAgentName}</div>}
            {resolvedUserPhone && <div className="rv-office-contact"><span>טל': {resolvedUserPhone}</span></div>}
            {resolvedEmail && <div className="rv-office-contact"><span>{resolvedEmail}</span></div>}
            {resolvedWebsite && <div className="rv-office-contact"><span>{resolvedWebsite}</span></div>}
            {resolvedAddress && <div className="rv-office-contact"><span>{resolvedAddress}</span></div>}
          </div>
        </div>
        <div className="rv-header-divider" />
      </div>

      {/* ── Section 2: Report Header ── */}
      <div className="rv-section rv-report-header">
        <h1 className="rv-report-title">דו"ח דירוג והערכת נכס</h1>
        <div className="rv-report-meta">
          {propertyAddress && <div className="rv-meta-row"><span className="rv-meta-label">כתובת:</span> {propertyAddress}</div>}
          <div className="rv-meta-row"><span className="rv-meta-label">עיר:</span> {city}</div>
          {neighborhood && <div className="rv-meta-row"><span className="rv-meta-label">שכונה:</span> {neighborhood}</div>}
          {propertyType && <div className="rv-meta-row"><span className="rv-meta-label">סוג נכס:</span> {propertyTypeLabel(propertyType)}</div>}
          <div className="rv-meta-row"><span className="rv-meta-label">תאריך דו"ח:</span> {reportDate}</div>
        </div>
      </div>

      {/* ── Section 3: Overall Score ── */}
      <div className="rv-section rv-score-block">
        <h2 className="rv-section-title">ציון כללי</h2>
        <div className="rv-overall-score">
          <span className="rv-score-value" dir="ltr">
            <span className="rv-score-number" style={{ color: scoreColor(result.scoreLabel) }}>{result.totalScore}</span>
            <span className="rv-score-max"> / 100</span>
          </span>
          <div className="rv-score-label" style={{ color: scoreColor(result.scoreLabel) }}>{result.scoreLabel}</div>
        </div>
        <div className="rv-component-scores">
          {result.breakdown.map(item => {
            const pct = Math.round((item.score / item.maxScore) * 100);
            return (
              <div key={item.parameter} className="rv-component-row">
                <div className="rv-component-label-row">
                  <span className="rv-component-label">{item.parameter}</span>
                  <span className="rv-component-value rv-score-value" dir="ltr">{item.score} / {item.maxScore}</span>
                </div>
                <div className="rv-bar-track">
                  <div className="rv-bar-fill" style={{ width: `${pct}%`, backgroundColor: barColor(pct) }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Section 4: Quality Breakdown ── */}
      <div className="rv-section rv-quality-section rv-page-break-avoid">
        <h2 className="rv-section-title">פירוט איכות הנכס</h2>
        <div className="rv-quality-header">
          <span>ציון איכות הנכס: </span>
          <span className="rv-quality-score rv-score-value" dir="ltr">{result.qualityResult.qualityScore} / 100</span>
          <span className="rv-quality-label"> ({result.qualityResult.qualityLabel})</span>
        </div>
        <div className="rv-quality-breakdown">
          {result.qualityResult.breakdown.map((qi: QualityBreakdownItem) => {
            const pct = Math.round((qi.score / qi.maxScore) * 100);
            return (
              <div key={qi.parameter} className="rv-quality-row">
                <div className="rv-quality-label-row">
                  <span className="rv-quality-param">{qi.parameter}</span>
                  <span className="rv-quality-val rv-score-value" dir="ltr">{Math.round(qi.score)} / {qi.maxScore}</span>
                </div>
                <div className="rv-bar-track">
                  <div className="rv-bar-fill" style={{ width: `${pct}%`, backgroundColor: barColor(pct) }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Section 5: Strengths & Weaknesses ── */}
      <div className="rv-section rv-strengths-weaknesses rv-page-break-avoid">
        <h2 className="rv-section-title">חוזקות וחולשות</h2>
        <div className="rv-sw-columns">
          <div className="rv-sw-column rv-strengths">
            <h3 className="rv-sw-title rv-strengths-title">חוזקות</h3>
            <ul className="rv-sw-list">
              {strengths.length === 0 && nsStrengths.length === 0 ? (
                <li className="rv-sw-empty">—</li>
              ) : (
                [...strengths, ...nsStrengths].map((s, i) => (
                  <li key={i} className="rv-sw-item rv-strength-item">{s}</li>
                ))
              )}
            </ul>
          </div>
          <div className="rv-sw-column rv-weaknesses">
            <h3 className="rv-sw-title rv-weaknesses-title">חולשות</h3>
            <ul className="rv-sw-list">
              {weaknesses.length === 0 && nsWeaknesses.length === 0 ? (
                <li className="rv-sw-empty">—</li>
              ) : (
                [...weaknesses, ...nsWeaknesses].map((w, i) => (
                  <li key={i} className="rv-sw-item rv-weakness-item">{w}</li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* ── Section 6: Neighborhood Analysis ── */}
      {nsRow && (
        <div className="rv-section rv-neighborhood-section rv-page-break-avoid">
          <h2 className="rv-section-title">ניתוח שכונה</h2>
          <div className="rv-neighborhood-grid">
            {nsRow.location_level && (
              <div className="rv-stat-row">
                <span className="rv-stat-label">רמת מיקום:</span>
                <span className="rv-stat-value">{nsRow.location_level}</span>
              </div>
            )}
            <div className="rv-stat-row">
              <span className="rv-stat-label">ציון מיקום:</span>
              <span className="rv-stat-value rv-score-value" dir="ltr">{nsRow.location_score} / 23</span>
            </div>
            {nsRow.confidence && (
              <div className="rv-stat-row">
                <span className="rv-stat-label">רמת ודאות:</span>
                <span className="rv-stat-value">{confidenceLabel(nsRow.confidence)}</span>
              </div>
            )}
          </div>
          {nsRow.reason && (
            <p className="rv-neighborhood-summary">{nsRow.reason}</p>
          )}
          {nsStrengths.length > 0 && (
            <div className="rv-neighborhood-sw">
              <h4 className="rv-sw-subtitle rv-strengths-title">חוזקות השכונה:</h4>
              <ul className="rv-sw-list">
                {nsStrengths.map((s, i) => <li key={i} className="rv-sw-item rv-strength-item">{s}</li>)}
              </ul>
            </div>
          )}
          {nsWeaknesses.length > 0 && (
            <div className="rv-neighborhood-sw">
              <h4 className="rv-sw-subtitle rv-weaknesses-title">חולשות השכונה:</h4>
              <ul className="rv-sw-list">
                {nsWeaknesses.map((w, i) => <li key={i} className="rv-sw-item rv-weakness-item">{w}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Section 7: Market Statistics ── */}
      {statsRow && (
        <div className="rv-section rv-market-section rv-page-break-avoid">
          <h2 className="rv-section-title">נתוני שוק לפי שכונה</h2>
          <div className="rv-market-grid">
            <div className="rv-stat-row">
              <span className="rv-stat-label">מחיר ממוצע למ"ר (מקורות נדל"ן):</span>
              <span className="rv-stat-value rv-money-value">{statsRow.avg_price_per_sqm != null ? formatPrice(statsRow.avg_price_per_sqm) : '—'}</span>
            </div>
            <div className="rv-stat-row">
              <span className="rv-stat-label">חציון עסקאות בפועל:</span>
              <span className="rv-stat-value rv-money-value">{statsRow.median_price_per_sqm_transactions != null ? formatPrice(statsRow.median_price_per_sqm_transactions) : '—'}</span>
            </div>
            <div className="rv-stat-row">
              <span className="rv-stat-label">ממוצע עסקאות בפועל:</span>
              <span className="rv-stat-value rv-money-value">{statsRow.avg_price_per_sqm_transactions != null ? formatPrice(statsRow.avg_price_per_sqm_transactions) : '—'}</span>
            </div>
            <div className="rv-stat-row">
              <span className="rv-stat-label">מספר עסקאות:</span>
              <span className="rv-stat-value">{statsRow.transactions_count_12m ?? statsRow.transactions_count ?? '—'}</span>
            </div>
            <div className="rv-stat-row">
              <span className="rv-stat-label">תקופת נתונים:</span>
              <span className="rv-stat-value">{periodLabel(statsRow.transactions_stats_period || statsRow.data_period) || '—'}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 8: Pricing Analysis ── */}
      {pricing && (
        <div className="rv-section rv-pricing-section rv-page-break-avoid">
          <h2 className="rv-section-title">הערכת תמחור נכס</h2>
          <div className="rv-pricing-grid">
            <div className="rv-stat-row">
              <span className="rv-stat-label">מחיר מבוקש:</span>
              <span className="rv-stat-value rv-money-value">{formatPrice(pricing.askingPrice)}</span>
            </div>
            <div className="rv-stat-row">
              <span className="rv-stat-label">מחיר למ"ר:</span>
              <span className="rv-stat-value rv-money-value">{formatPrice(pricing.pricePerSqm)}</span>
            </div>
            <div className="rv-stat-row">
              <span className="rv-stat-label">מחיר ייחוס ({pricing.benchmarkLabel}):</span>
              <span className="rv-stat-value rv-money-value">{formatPrice(pricing.benchmarkPerSqm)}</span>
            </div>
            <div className="rv-stat-row">
              <span className="rv-stat-label">פער מהשוק:</span>
              <span className="rv-stat-value rv-pricing-gap rv-percent-value">
                <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{pricing.gapPercent > 0 ? '+' : ''}{pricing.gapPercent.toFixed(1)}%</span>
              </span>
            </div>
            <div className="rv-stat-row rv-pricing-status-row">
              <span className="rv-stat-label">סטטוס תמחור:</span>
              <span className="rv-pricing-status">{pricing.status}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 9: Recommended RV Price ── */}
      {rvRecommended && (
        <div className="rv-section rv-recommended-section rv-page-break-avoid">
          <h2 className="rv-section-title">מחיר מומלץ RV</h2>
          <div className="rv-recommended-card">
            <div className="rv-recommended-grid">
              <div className="rv-stat-row">
                <span className="rv-stat-label">מחיר בסיס לפי השוק:</span>
                <span className="rv-stat-value rv-money-value">{formatPrice(rvRecommended.baseMarketPrice)}</span>
              </div>
              <div className="rv-stat-row">
                <span className="rv-stat-label">ציון איכות הנכס:</span>
                <span className="rv-stat-value rv-score-value" dir="ltr">{rvRecommended.qualityScore} / 100</span>
              </div>
              <div className="rv-stat-row">
                <span className="rv-stat-label">התאמת איכות:</span>
                <span className="rv-stat-value rv-percent-value" dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{rvRecommended.qualityAdjustmentPercent > 0 ? '+' : ''}{rvRecommended.qualityAdjustmentPercent}%</span>
              </div>
              <div className="rv-stat-row rv-recommended-price-row">
                <span className="rv-stat-label rv-recommended-label">מחיר מומלץ RV:</span>
                <span className="rv-recommended-price rv-money-value">{formatPrice(rvRecommended.recommendedPrice)}</span>
              </div>
              <div className="rv-stat-row">
                <span className="rv-stat-label">פער מול מחיר מבוקש:</span>
                <span className="rv-stat-value rv-pricing-gap rv-percent-value">
                  <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{rvRecommended.recommendedGapPercent > 0 ? '+' : ''}{rvRecommended.recommendedGapPercent.toFixed(1)}%</span>
                </span>
              </div>
            </div>
            <p className="rv-disclaimer">
              המחיר המומלץ מבוסס על עסקאות שבוצעו בפועל בשכונה ועל מאפייני הנכס. זהו כלי תומך החלטה ואינו מהווה הערכת שמאי.
            </p>
          </div>
        </div>
      )}

      {/* ── Section 9.5: AI Advisor ── */}
      {advisor && (
        <div className="rv-section rv-advisor-section rv-page-break-avoid">
          <h2 className="rv-section-title">יועץ AI לנכס</h2>
          {advisor.executive_summary && (
            <div className="rv-advisor-block">
              <p className="rv-advisor-label">סיכום מנהלים</p>
              <p className="rv-advisor-text">{advisor.executive_summary}</p>
            </div>
          )}
          {advisor.marketability_assessment && (
            <div className="rv-advisor-block">
              <p className="rv-advisor-label">פוטנציאל שיווקי</p>
              <div style={{
                display: 'inline-block',
                padding: '2mm 3mm',
                borderRadius: '3mm',
                borderWidth: '0.5mm',
                borderStyle: 'solid',
                borderColor: advisor.marketability_assessment.level.includes('גבוה') ? '#16a34a' : advisor.marketability_assessment.level.includes('בינוני') ? '#ca8a04' : '#dc2626',
              }}>
                <span style={{
                  fontWeight: 700,
                  fontSize: '10pt',
                  color: advisor.marketability_assessment.level.includes('גבוה') ? '#16a34a' : advisor.marketability_assessment.level.includes('בינוני') ? '#ca8a04' : '#dc2626',
                }}>{advisor.marketability_assessment.level}</span>
              </div>
              <p className="rv-advisor-text" style={{ marginTop: '1.5mm' }}>{advisor.marketability_assessment.explanation}</p>
            </div>
          )}
          {advisor.key_selling_points && advisor.key_selling_points.length > 0 && (
            <div className="rv-advisor-block">
              <p className="rv-advisor-label">נקודות מכירה מרכזיות</p>
              <ul className="rv-sw-list">
                {advisor.key_selling_points.map((pt, i) => <li key={i} className="rv-sw-item rv-strength-item">{pt}</li>)}
              </ul>
            </div>
          )}
          {advisor.risk_factors && advisor.risk_factors.length > 0 && (
            <div className="rv-advisor-block">
              <p className="rv-advisor-label">גורמי סיכון</p>
              <ul className="rv-sw-list">
                {advisor.risk_factors.map((rf, i) => <li key={i} className="rv-sw-item rv-weakness-item">{rf}</li>)}
              </ul>
            </div>
          )}
          {advisor.recommended_marketing_strategy && (
            <div className="rv-advisor-block">
              <p className="rv-advisor-label">אסטרטגיית שיווק מומלצת</p>
              <p className="rv-advisor-text">{advisor.recommended_marketing_strategy}</p>
            </div>
          )}
          {advisor.pricing_recommendation && (
            <div className="rv-advisor-block">
              <p className="rv-advisor-label">המלצת תמחור</p>
              <p className="rv-advisor-text">{advisor.pricing_recommendation}</p>
            </div>
          )}
          {advisor.action_items && advisor.action_items.length > 0 && (
            <div className="rv-advisor-block">
              <p className="rv-advisor-label">פעולות מומלצות</p>
              <ul className="rv-sw-list">
                {advisor.action_items.map((ai, i) => <li key={i} className="rv-sw-item">{ai}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Section 10: Comparable Transactions ── */}
      {comparables.length > 0 && (
        <div className="rv-section rv-comparables-section rv-page-break-avoid">
          <h2 className="rv-section-title">עסקאות אחרונות בשכונה</h2>
          <table className="rv-comparables-table">
            <thead>
              <tr>
                <th>תאריך</th>
                <th>כתובת</th>
                <th>חד'</th>
                <th>שטח</th>
                <th>מחיר</th>
                <th>מ"ר</th>
              </tr>
            </thead>
            <tbody>
              {comparables.map((tx, i) => (
                <tr key={i}>
                  <td className="rv-td-nowrap">{tx.transaction_date ? formatDate(tx.transaction_date) : '—'}</td>
                  <td>{tx.full_address || '—'}</td>
                  <td>{tx.rooms ?? '—'}</td>
                  <td>{tx.size_sqm != null ? `${tx.size_sqm} מ"ר` : '—'}</td>
                  <td className="rv-td-nowrap">{tx.price != null ? formatPrice(tx.price) : '—'}</td>
                  <td className="rv-td-nowrap">{tx.price_per_sqm != null ? formatPrice(tx.price_per_sqm) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Footer ── */}
      <div className="rv-footer">
        <div className="rv-footer-left">Generated by Realty Value (RV) · Property Rating Report · v1.0</div>
      </div>
    </div>
  );
}

export default PropertyRatingPrintReport;